// File: src/pages/POReceivingPage.jsx

import React, {
  useMemo,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import POReceivingPanel from "@/components/Procurement/POReceivingPanel";

/* =============================================================================
   PO RECEIVING PAGE

   Purpose:
   - Dedicated Goods Receiving workspace for one PO
   - Keep Receiving users inside receiving workflows only
   - Business logic remains inside POReceivingPanel
   - Backend remains authoritative for PO visibility/action permissions
============================================================================= */

export default function POReceivingPage() {
  const {
    poId,
  } =
    useParams();

  /* ---------------------------------------------------------------------------
     Safely resolve PO ID
  --------------------------------------------------------------------------- */

  const poIdNum =
    useMemo(() => {
      const n =
        Number.parseInt(
          String(
            poId ??
              ""
          ),
          10
        );

      return (
        Number.isInteger(
          n
        ) &&
        n > 0
      )
        ? n
        : null;
    }, [
      poId,
    ]);

  /* ---------------------------------------------------------------------------
     Invalid route
  --------------------------------------------------------------------------- */

  if (
    !poIdNum
  ) {
    return (
      <div className="min-h-screen bg-[#080d1f] p-6 text-white grid place-items-center">
        <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-white/5 p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/30 bg-amber-500/10 text-2xl">
            ⚠️
          </div>

          <h1 className="text-2xl font-bold">
            Invalid Purchase Order
          </h1>

          <p className="mt-3 text-sm leading-6 text-slate-300">
            SOLO.MDSH could not read a valid purchase order number from this link.
          </p>

          <Link
            to="/po-receiving-dashboard"
            className="mt-6 inline-flex items-center rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-5 py-3 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-500/20"
          >
            ← Return to Receiving Dashboard
          </Link>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------------------
     Page
  --------------------------------------------------------------------------- */

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#080d1f] via-[#0a1026] to-[#161b38] text-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {/* =====================================================================
            Navigation
        ===================================================================== */}

        <div className="mb-5 flex flex-wrap items-center gap-2">
          <Link
            to="/po-receiving-dashboard"
            className="inline-flex items-center rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-white/20 hover:bg-white/10"
          >
            ← Receiving Dashboard
          </Link>

          <Link
            to="/receiving-activity"
            className="inline-flex items-center rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-200 transition hover:border-cyan-400/40 hover:bg-cyan-500/20"
          >
            Receiving Activity
          </Link>
        </div>

        {/* =====================================================================
            Header
        ===================================================================== */}

        <section className="mb-6 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.045] shadow-2xl">
          <div className="border-b border-white/10 p-5 sm:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300/80">
                  Goods Receiving
                </div>

                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Receive Purchase Order
                </h1>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 font-mono text-sm font-semibold text-cyan-200">
                    PO #{poIdNum}
                  </span>

                  <span className="text-sm text-slate-400">
                    Line-level receiving workspace
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Workflow
                  </div>

                  <div className="mt-1 text-sm font-semibold text-slate-200">
                    Receiving
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Audit
                  </div>

                  <div className="mt-1 text-sm font-semibold text-slate-200">
                    Line Level
                  </div>
                </div>

                <div className="col-span-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 sm:col-span-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Evidence
                  </div>

                  <div className="mt-1 text-sm font-semibold text-slate-200">
                    Enabled
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ------------------------------------------------------------------
              Operational guidance
          ------------------------------------------------------------------ */}

          <div className="grid gap-3 p-5 sm:grid-cols-3 sm:p-6">
            <div className="rounded-2xl border border-emerald-400/15 bg-emerald-500/[0.06] p-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-300/80">
                Quantity
              </div>

              <p className="text-sm leading-5 text-slate-300">
                Record only the quantity physically received during this delivery stage.
              </p>
            </div>

            <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/[0.06] p-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-cyan-300/80">
                Temperature
              </div>

              <p className="text-sm leading-5 text-slate-300">
                Record temperature against the individual line whenever temperature control is required.
              </p>
            </div>

            <div className="rounded-2xl border border-violet-400/15 bg-violet-500/[0.06] p-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-300/80">
                Evidence
              </div>

              <p className="text-sm leading-5 text-slate-300">
                Photos, exception reasons, backorders and rejection actions are captured against the relevant item.
              </p>
            </div>
          </div>
        </section>

        {/* =====================================================================
            Receiving workflow

            POReceivingPanel owns:
            - PO item retrieval
            - quantity receiving
            - temperature
            - reject / credit / ignore
            - backorder / reorder
            - evidence photos
            - action permissions
            - submission
            - activity timeline
        ===================================================================== */}

        <POReceivingPanel
          poId={
            poIdNum
          }
        />

        {/* =====================================================================
            Footer navigation
        ===================================================================== */}

        <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-5 text-slate-500">
            Receiving transactions are recorded against the authenticated user
            and retained as part of the purchase order audit history.
          </p>

          <div className="flex flex-wrap gap-2">
            <Link
              to="/po-receiving-dashboard"
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-300 transition hover:bg-white/10"
            >
              Receiving Dashboard
            </Link>

            <Link
              to="/receiving-activity"
              className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-500/20"
            >
              View Receiving Activity →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}